<template>
  <div class="publish-diff-block">
    <DiffActionHeadline
      class="publish-diff-block__headline"
      :parts="block.titleParts"
      :fallback="titleFallback"
      :keyword="keyword"
    />
    <slot name="after-headline" />
    <template v-if="showRows">
      <div
        v-for="row in rows"
        :key="row.rowKey"
        class="publish-diff-block__diff"
      >
        <div
          v-if="row.kind === 'tableColumnChange'"
          class="publish-diff-block__headline"
        >
          {{ row.actionLabel }} Data Table
          <code
            :class="{
              'ant-tag-green': row.actionLabel === '新增',
              'ant-tag-red': row.actionLabel === '刪除',
            }"
          >
            <SearchHighlightText :text="row.colTitle" :keyword="keyword" />
          </code>
          Column
        </div>
        <div
          v-else-if="row.tableColumnHeading"
          class="publish-diff-block__headline"
        >
          更新 Data Table
          <code>
            <SearchHighlightText :text="row.colTitle" :keyword="keyword" />
          </code>
          Column:
          <DiffValueCompare
            :oldValue="row.oldText"
            :newValue="row.newText"
            :mode="row.mode"
            :keyword="keyword"
          />
        </div>
        <div
          v-else-if="row.kind === 'example'"
          class="publish-diff-block__headline"
        >
          {{ row.actionLabel }}{{ row.isField ? '參數' : '' }} Example
          <code :class="row.nameClass">
            <SearchHighlightText :text="row.exampleName" :keyword="keyword" />
          </code>
          <template v-if="row.platforms">
            :
            <DiffTagListCompare
              fieldKey="platforms"
              :oldValue="row.oldValue"
              :newValue="row.newValue"
              :getLabel="getPlatformLabel"
              emptyText="ALL"
              :keyword="keyword"
            />
          </template>
          <template v-else-if="row.showValue">
            :
            <DiffValueCompare
              :oldValue="row.oldText"
              :newValue="row.newText"
              :mode="row.mode"
              :keyword="keyword"
            />
          </template>
        </div>
        <div
          v-else
          class="publish-diff-block__row"
          :class="{ 'publish-diff-block__row--content': isContentRow(row) }"
        >
          <span
            v-if="rowLabel(row)"
            class="publish-diff-block__label"
          >
            <SearchHighlightText :text="rowLabel(row)" :keyword="keyword" />:
          </span>
          <template v-if="row.kind === 'content' || isTableColumnEditor(row)">
            <DiffContentViewerCompare
              :key="row.rowKey"
              class="publish-diff-block__content"
              :oldValue="row.oldValue"
              :newValue="row.newValue"
              :type="row.diffType || block.type"
              :keyword="keyword"
            />
          </template>
          <template v-else-if="row.kind === 'image'">
            <div class="publish-diff-block__thumb-wrap">
              <DiffImageThumb
                v-if="row.oldSrc"
                :src="row.oldSrc"
                :alt="row.label"
                variant="old"
              />
              <span
                v-if="row.oldSrc && row.newSrc"
                class="publish-diff-block__arrow"
              >=></span>
              <DiffImageThumb
                v-if="row.newSrc"
                :src="row.newSrc"
                :alt="row.label"
                variant="new"
              />
            </div>
          </template>
          <template v-else-if="row.kind === 'docs' || row.kind === 'platforms'">
            <DiffTagListCompare
              :fieldKey="row.fieldKey"
              :oldValue="row.oldValue"
              :newValue="row.newValue"
              :getLabel="row.kind === 'docs' ? getDocLabel : getPlatformLabel"
              :emptyText="row.emptyText"
              :keyword="keyword"
            />
          </template>
          <template v-else-if="String(row.path || '').startsWith('/field') && row.key === 'sortOrder'">
            <DiffTagListCompare
              :fieldKey="row.key"
              :oldValue="row.oldValue"
              :newValue="row.newValue"
              :keyword="keyword"
            />
          </template>
          <template v-else-if="row.key === 'sortOrder' && resolveSortOrderLabel">
            <code class="ant-tag-red">{{ resolveSortOrderLabel(row, 'old') }}</code>
            <span class="publish-diff-block__arrow">=></span>
            <code class="ant-tag-green">{{ resolveSortOrderLabel(row, 'new') }}</code>
          </template>
          <template v-else-if="row.key === 'parentSectionId' && resolveParentTitle">
            <code class="ant-tag-red">{{ resolveParentTitle(row.oldValue, 'old') }}</code>
            <span class="publish-diff-block__arrow">=></span>
            <code class="ant-tag-green">{{ resolveParentTitle(row.newValue, 'new') }}</code>
          </template>
          <template v-else-if="isTableColumnPlatform(row)">
            <code class="ant-tag-red">{{ getPlatformLabel(row.oldValue) }}</code>
            <span class="publish-diff-block__arrow">=></span>
            <code class="ant-tag-green">{{ getPlatformLabel(row.newValue) }}</code>
          </template>
          <template v-else-if="isTableColumnArray(row)">
            <DiffTagListCompare
              :fieldKey="row.key"
              :oldValue="row.oldValue"
              :newValue="row.newValue"
              :getLabel="item => item"
              :keyword="keyword"
            />
          </template>
          <template v-else-if="row.kind === 'objectMap'">
            <div class="publish-diff-block__object-map">
              <div>
                <code
                  v-for="[mapKey, mapVal] in row.oldEntries"
                  :key="`old-${mapKey}`"
                  class="ant-tag-red"
                >{{ mapKey }}: {{ mapVal === undefined ? '' : mapVal }}</code>
              </div>
              <span class="publish-diff-block__arrow">=></span>
              <div>
                <code
                  v-for="[mapKey, mapVal] in row.newEntries"
                  :key="`new-${mapKey}`"
                  class="ant-tag-green"
                >{{ mapKey }}: {{ mapVal === undefined ? '' : mapVal }}</code>
              </div>
            </div>
          </template>
          <template v-else>
            <DiffValueCompare
              :oldValue="row.oldText"
              :newValue="row.newText"
              :mode="row.mode"
              :keyword="keyword"
            />
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  h,
  ref,
  watch,
  computed,
} from 'vue'

import FileExcelOutlined from '@ant-design/icons-vue/lib/icons/FileExcelOutlined'

import DiffActionHeadline from '@/components/ApiDoc/publish/DiffActionHeadline.vue'
import DiffContentViewerCompare from '@/components/ApiDoc/shared/DiffContentViewerCompare.vue'
import SearchHighlightText from '@/components/ApiDoc/shared/SearchHighlightText.vue'
import DiffTagListCompare from '@/components/ApiDoc/shared/DiffTagListCompare.vue'
import DiffValueCompare from '@/components/ApiDoc/shared/DiffValueCompare.vue'

import { compactPublishDiffRows } from './compactPublishDiffRows'

const identity = id => id

const DiffImageThumb = {
  name: 'DiffImageThumb',
  props: {
    src: { type: String, default: '' },
    alt: { type: String, default: '' },
    variant: { type: String, default: 'new' },
  },
  setup(props) {
    const broken = ref(false)
    watch(() => props.src, () => { broken.value = false })
    const onError = () => { broken.value = true }

    return () => h(
      'span',
      {
        class: [
          'publish-diff-block__thumb',
          `publish-diff-block__thumb--${props.variant || 'new'}`,
        ],
      },
      props.src && !broken.value
        ? [h('img', {
          src: props.src,
          alt: props.alt,
          onError,
        })]
        : [h(FileExcelOutlined, { title: '圖片連結已失效' })],
    )
  },
}

export default {
  name: 'PublishDiffBlock',
  components: {
    DiffActionHeadline,
    DiffContentViewerCompare,
    SearchHighlightText,
    DiffTagListCompare,
    DiffValueCompare,
    DiffImageThumb,
  },
  props: {
    block: { type: Object, default: () => ({}) },
    keyword: { type: String, default: '' },
    getDocLabel: { type: Function, default: identity },
    getPlatformLabel: { type: Function, default: identity },
    resolveSortOrderLabel: { type: Function, default: undefined },
    resolveParentTitle: { type: Function, default: undefined },
    getTableColumn: { type: Function, default: undefined },
  },
  setup(props) {
    const rows = computed(() => compactPublishDiffRows(props.block || {}))
    const titleFallback = computed(() => props.block?.title?.default || '')
    const showRows = computed(() => {
      const block = props.block || {}
      return block.type === 'modified' || (block.diffs || []).some(d => d && d.key)
    })

    const tableColumn = (key) => {
      if (!key) return null
      const fromProp = props.getTableColumn?.(key)
      if (fromProp) return fromProp
      return props.block?.tableColumns?.[key] || props.block?.beforeTableColumns?.[key] || null
    }
    const isTableColumnEditor = row => tableColumn(row.key)?.element === 'editor'
    const isTableColumnPlatform = row => !!(tableColumn(row.key) && row.key === 'platform')
    const isTableColumnArray = row => !!(
      tableColumn(row.key)
      && (Array.isArray(row.oldValue) || Array.isArray(row.newValue))
    )
    const isContentRow = row => row.kind === 'content' || isTableColumnEditor(row)
    const rowLabel = (row) => {
      if (row.kind === 'tableColumnChange' || row.tableColumnHeading || row.kind === 'example') return ''
      if (row.key === 'sortOrder' && String(row.path || '').startsWith('/field')) return ''
      return row.label || ''
    }

    return {
      rows,
      titleFallback,
      showRows,
      isTableColumnEditor,
      isTableColumnPlatform,
      isTableColumnArray,
      isContentRow,
      rowLabel,
    }
  },
}
</script>

<style>
.publish-diff-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.publish-diff-block__headline {
  display: block;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.publish-diff-block__headline code {
  white-space: pre-wrap;
  word-break: break-word;
}

.publish-diff-block__diff {
  margin-left: 16px;
  min-width: 0;
}

.publish-diff-block__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  width: 100%;
}

.publish-diff-block__row--content {
  flex-direction: column;
  align-items: stretch;
}

.publish-diff-block__content {
  min-width: 0;
  width: 100%;
}

.publish-diff-block__label {
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.65);
}

.publish-diff-block__object-map {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.publish-diff-block__object-map code {
  display: block;
}

.publish-diff-block__arrow {
  margin: 0 4px;
  color: rgba(0, 0, 0, 0.45);
}

.publish-diff-block__thumb-wrap {
  display: inline-flex;
  align-items: center;
}

.publish-diff-block__thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  max-width: 120px;
  max-height: 32px;
  padding: 2px;
  vertical-align: middle;
  background: #fff;
  border-radius: 2px;
}

.publish-diff-block__thumb img {
  max-width: 120px;
  max-height: 28px;
  object-fit: contain;
}

.publish-diff-block__thumb .anticon {
  width: 32px;
  font-size: 22px;
  color: #ddd;
}

.publish-diff-block__thumb--old {
  border: 1px solid #ffa39e;
}

.publish-diff-block__thumb--new {
  border: 1px solid #b7eb8f;
}
</style>
